<template>
  <div class="group flex flex-col items-center">
    <FileInput
      id="file-upload"
      ref="fileInput"
      hidden
      :accept="accept"
      :maxSize="maxSize"
      :multiple="multiple"
      :limit="limit"
      :disabled="disabled"
      @input="onInput"
    />
    <label
      v-if="showDropZone"
      for="file-upload"
      ref="dropZone"
      class="w-full flex flex-col grow gap-3 items-center justify-center px-4 py-6 bg-primary border border-secondary rounded-xl cursor-pointer transition-all hover:bg-tertiary"
      :class="{
        'border-utility-brand-700 bg-tertiary outline-2 outline-dashed outline-offset-2 outline-utility-brand-700':
          isOverDropZone,
        'pointer-events-none': disabled,
      }"
    >
      <span
        class="flex flex-col gap-1 items-center justify-center text-tertiary"
      >
        <span
          class="flex items-center justify-center text-style-sm gap-1 pointer-events-none"
        >
          <a href="#" class="button-tertiary-color-fg font-semibold">
            {{ $t(`admin.files.upload.drop_zone.click`) }}
          </a>
          <span v-if="!maxSmall" class="text-tertiary font-normal">
            {{ $t(`admin.files.upload.drop_zone.drag_and_drop`) }}
          </span>
        </span>
        <span class="flex items-center justify-center text-style-sm gap-1">
          {{
            $t("admin.files.upload.drop_zone.file_restrictions", {
              size: formatBytes(fileInput?.maxSize),
            })
          }}
        </span>
      </span>
    </label>
  </div>
</template>
<script setup>
import { ref, computed, watch } from "vue";
import { useForm } from "@inertiajs/vue3";
import { useDropZone } from "@vueuse/core";
import { uniqueId } from "lodash";
import { FileInput } from "@/SharedComponents/Inputs";
import { toast } from "@/SharedComponents/Toaster";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { formatBytes } from "@/Utils/size";
import { useI18n } from "vue-i18n";

const { maxSmall } = useBreakpoints();
const { t } = useI18n();

const emit = defineEmits(["filesSelected"]);

const props = defineProps({
  allFiles: { type: Array, default: () => [] },
  buildingId: { type: String, required: true },
  accept: { type: Array, required: false },
  maxSize: { type: Number, required: false },
  multiple: { type: Boolean, required: false },
  limit: { type: Number, required: false },
  disabled: { type: Boolean, default: false },
});

const fileInput = ref();
const errors = ref({});
const infos = ref({});

const dropZone = ref();
const { isOverDropZone } = useDropZone(dropZone, {
  onDrop: (files) => onInput(files),
  dataTypes: props.accept,
  multiple: props.multiple,
});

const showDropZone = computed(() => {
  return (
    (!props.limit || props.allFiles.length < props.limit) &&
    (props.multiple || props.allFiles.length === 0)
  );
});

const onInput = (files) => {
  let filteredFiles = filterValidFiles(files);

  emit(
    "filesSelected",
    filteredFiles.map((file) =>
      useForm({
        id: uniqueId(),
        title: file.name,
        tenant_id: props.buildingId,
        size: file.size,
        lastModified: file.lastModified,
        lastModifiedDate: file.lastModifiedDate,
        type: file.type,
        document: file,
        uploadProgress: 0,
      })
    )
  );
};

function filterValidFiles(files) {
  let unacceptedFiles = [];
  let oversizedFiles = [];
  let overCountLimitFiles = [];
  let validFiles = [];
  let _errors = {};
  let _infos = {};

  for (const file of files) {
    if (props.accept?.length && !props.accept.includes(file.type)) {
      unacceptedFiles.push(file.name);
    } else if (file.size > props.maxSize) {
      oversizedFiles.push(file.name);
    } else if (!props.multiple) {
      validFiles = [file];
      unacceptedFiles = [];
      oversizedFiles = [];
    } else if (!props.limit || validFiles.length < props.limit) {
      validFiles.push(file);
    } else if (props.limit && validFiles.length === props.limit) {
      overCountLimitFiles.push(file.name);
    }
  }

  if (unacceptedFiles.length > 0) {
    Object.assign(_errors, {
      file_type: t(
        "components.errors.files.upload.unaccepted_type_selected",
        {
          files: unacceptedFiles.join(", "),
        },
        unacceptedFiles.length
      ),
    });
  }

  if (oversizedFiles.length > 0) {
    Object.assign(_errors, {
      file_size: t(
        "components.errors.files.upload.size_limit_exceeded",
        {
          limit: formatBytes(props.maxSize),
          files: oversizedFiles.join(", "),
        },
        oversizedFiles.length
      ),
    });
  }

  if (overCountLimitFiles.length > 0) {
    Object.assign(_infos, {
      file_size: t(
        "components.errors.files.upload.count_limit_exceeded",
        {
          limit: props.limit,
          limit_surplass: overCountLimitFiles.length,
        },
        overCountLimitFiles.length
      ),
    });
  }

  errors.value = _errors;
  infos.value = _infos;

  return validFiles;
}

watch(
  () => fileInput.value?.errors,
  (errors) => errors && showToast("error", errors)
);
watch(
  () => errors.value,
  (errors) => errors && showToast("error", errors)
);

watch(
  () => fileInput.value?.infos,
  (infos) => infos && showToast("info", infos)
);

watch(
  () => infos.value,
  (infos) => infos && showToast("info", infos)
);

function showToast(type, messages) {
  Object.values(messages).forEach((message) => {
    toast[type]({ title: message, duration: 8000 });
  });
}

defineExpose({
  maxSize: props.maxSize,
  errors,
  infos,
  filterValidFiles,
  onInput,
});
</script>
